import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeHighlight from "rehype-highlight";
import Modal from "../ui/Modal";
import type { Announcement } from "../../types/announcement";

export default function AnnouncementDetailModal({
  isOpen,
  onClose,
  announcement,
}: {
  isOpen: boolean;
  onClose: () => void;
  announcement: Announcement | null;
}) {
  if (!announcement) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Duyuru Detayı" size="lg">
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="text-lg font-semibold text-gray-900 break-words">{announcement.title}</div>
          <div className="mt-1 text-xs text-gray-500">
            {new Date(announcement.createdAt).toLocaleDateString("tr-TR", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
            {announcement.creator
              ? ` • ${(announcement.creator.firstName || "").trim()} ${(announcement.creator.lastName || "").trim()}`.trim()
              : ""}
          </div>

          {announcement.departments && announcement.departments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {announcement.departments.map((dept) => (
                <span
                  key={dept.id}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-700"
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: dept.color || "#9ca3af" }}
                    aria-hidden
                  />
                  <span className="max-w-[18rem] truncate">{dept.name}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 max-h-[70vh] overflow-y-auto">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeHighlight]}
            components={{
              a: ({ children, href }) => (
                <a
                  href={href}
                  className="text-blue-600 hover:underline font-medium"
                  style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {children}
                </a>
              ),
              h1: ({ children }) => <h2 className="mt-0 mb-3 text-xl font-semibold text-gray-900 break-words">{children}</h2>,
              h2: ({ children }) => <h3 className="mt-0 mb-2 text-lg font-semibold text-gray-900 break-words">{children}</h3>,
              h3: ({ children }) => <h4 className="mt-0 mb-2 text-base font-semibold text-gray-900 break-words">{children}</h4>,
              hr: () => <hr className="my-3 border-gray-200" />,
              p: ({ children }) => <p className="m-0 mb-3 last:mb-0 whitespace-pre-wrap break-words text-sm text-gray-800">{children}</p>,
              ul: ({ children }) => <ul className="list-disc ml-5 my-3 space-y-1 text-sm text-gray-800">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal ml-5 my-3 space-y-1 text-sm text-gray-800">{children}</ol>,
              li: ({ children }) => <li className="whitespace-pre-wrap break-words">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
              em: ({ children }) => <em className="italic">{children}</em>,
              pre: ({ children }) => (
                <pre className="my-3 max-h-[50vh] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-relaxed">
                  {children}
                </pre>
              ),
              code: ({ children, className }) => <code className={`break-words ${className || ""}`}>{children}</code>,
            }}
          >
            {announcement.message}
          </ReactMarkdown>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 font-medium text-sm"
          >
            Kapat
          </button>
        </div>
      </div>
    </Modal>
  );
}
